import React from 'react';
import { Box, Paper, Typography, Skeleton } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { usajiliApi } from '@services/api/usajili';
import { useUSAJILI } from '@hooks/useUSAJILI';
import { formatNumber } from '@utils/usajiliFormat';
import { USAJILITrendVotersTab } from './USAJILITrendVotersTab';
import { USAJILISummaryTab } from './USAJILISummaryTab';
import { USAJILIToDateTab } from './USAJILIToDateTab';

/**
 * Page 1 — Dashboard.
 *   Row 1: KPI cards — New IDs · ID Replacements · Voters Registered (total to date)
 *   Row 2: Line chart — No. registered by date
 *   Row 3: Summary table | Bar chart
 */
export const USAJILIOverviewTab: React.FC = () => {
  const { filters, refetchKey } = useUSAJILI();

  // Same query key as the Summary table, so react-query fetches it once.
  const { data, isLoading } = useQuery({
    queryKey: ['usajili_summary', filters, refetchKey],
    queryFn: () => usajiliApi.summary(filters),
  });
  const total = data?.rows?.find((r) => r.is_grand_total);

  const nice = (d: string) => dayjs(d).format('D MMM YYYY');
  const period = !filters.date_from && !filters.date_to
    ? 'Total to date'
    : filters.date_from && filters.date_from === filters.date_to
      ? `Total ${nice(filters.date_from)}`
      : `Total ${filters.date_from ? nice(filters.date_from) : 'start'} – ${filters.date_to ? nice(filters.date_to) : 'latest'}`;
  const scope = filters.county !== 'All' ? filters.county : 'All counties';

  const kpis = [
    { label: 'New IDs', value: total?.new_ids, color: '#1F77B4' },
    { label: 'ID Replacements', value: total?.id_replacements, color: '#FF7F0E' },
    { label: 'Voters Registered', value: total?.voters_registered, color: '#2CA02C' },
  ];

  // On desktop (lg+) the page fills exactly the space under the header —
  // KPIs, line chart, table and bar chart all visible at a glance.
  // Tablets and phones get a scrolling stack with fixed chart heights, so
  // the charts never get squeezed by a short viewport.
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        flex: { lg: 1 },
        minHeight: { lg: 0 },
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
        {kpis.map((k) => (
          <Paper key={k.label} sx={{ px: 2, py: 1, borderTop: `3px solid ${k.color}` }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', columnGap: 1 }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {k.label}
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.72rem', md: '0.8rem' }, fontWeight: 500, color: 'text.secondary', textAlign: 'right' }}>
                {period} · {scope}
              </Typography>
            </Box>
            {isLoading ? (
              <Skeleton width="50%" height={38} />
            ) : (
              <Typography sx={{ fontSize: { xs: '1.5rem', md: '1.75rem' }, fontWeight: 700, lineHeight: 1.25, color: 'text.primary' }}>
                {formatNumber(k.value ?? 0)}
              </Typography>
            )}
          </Paper>
        ))}
      </Box>

      <Box sx={{ flex: { lg: '1 1 0' }, height: { xs: 360, sm: 400, md: 440, lg: 'auto' }, minHeight: { lg: 0 } }}>
        <USAJILITrendVotersTab />
      </Box>

      <Box
        sx={{
          flex: { lg: '1.15 1 0' },
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 1.5,
        }}
      >
        <Box sx={{ minHeight: 0, height: { xs: 'auto', lg: '100%' } }}>
          <USAJILISummaryTab />
        </Box>
        {/* Phones: the bar chart turns horizontal and sizes itself to the county count. */}
        <Box sx={{ height: { xs: 'auto', sm: 420, md: 440, lg: '100%' }, minHeight: 0 }}>
          <USAJILIToDateTab />
        </Box>
      </Box>
    </Box>
  );
};
